import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

// Stage-specific config: icon, color, role label, action label
const STAGE_CONFIG = {
  'Pending Team Assignment': {
    icon: 'Users',
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/30',
    roleLabel: 'Coordinator',
    popupMessage: 'New request needs assignment.',
    actionLabel: 'Assign Engineer / Team',
    actionIcon: 'UserPlus',
  },
  'Calculated': {
    icon: 'Calculator',
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    border: 'border-blue-400/30',
    roleLabel: 'Estimator',
    popupMessage: 'Calculation completed, please review.',
    actionLabel: 'Review & Confirm Calculation',
    actionIcon: 'CheckSquare',
  },
  'Pending Approval': {
    icon: 'Clock',
    color: 'text-orange-400',
    bg: 'bg-orange-400/10',
    border: 'border-orange-400/30',
    roleLabel: 'Manager / Approver',
    popupMessage: 'Approval required.',
    actionLabel: 'Approve / Reject',
    actionIcon: 'ShieldCheck',
  },
  'Approved': {
    icon: 'CheckCircle',
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/30',
    roleLabel: 'Finance Team',
    popupMessage: 'Approved request ready for payment.',
    actionLabel: 'Prepare Payment',
    actionIcon: 'CreditCard',
  },
  'Ready for Payment': {
    icon: 'CreditCard',
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    border: 'border-purple-400/30',
    roleLabel: 'Finance Officer',
    popupMessage: 'Payment confirmation required.',
    actionLabel: 'Confirm & Execute Payment',
    actionIcon: 'BadgeCheck',
  },
  'Paid': {
    icon: 'Banknote',
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/30',
    roleLabel: 'Requester',
    popupMessage: 'Your request has been paid.',
    actionLabel: 'View Receipt',
    actionIcon: 'FileText',
  },
};

const DEFAULT_STAGE = {
  icon: 'Bell',
  color: 'text-primary',
  bg: 'bg-primary/10',
  border: 'border-primary/30',
  roleLabel: '',
  popupMessage: 'You have a pending task.',
  actionLabel: 'View Details',
  actionIcon: 'ArrowRight',
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

// ─── Single Notification Card ─────────────────────────────────────────────────

const NotificationCard = ({ notification, cfg, onAction, onTicketClick }) => {
  return (
    <div className={`mx-4 my-2 rounded-xl border ${cfg?.border} ${cfg?.bg} overflow-hidden`}>
      {/* Card header */}
      <div
        onClick={() => onTicketClick(notification)}
        className="flex items-start gap-3 p-3 cursor-pointer hover:bg-black/5 transition-colors"
      >
        <div className={`flex-shrink-0 mt-0.5 p-2 rounded-xl bg-background/60`}>
          <Icon name={cfg?.icon} size={14} className={cfg?.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {notification?.ticket_number && (
              <span className="text-[10px] font-mono font-bold text-foreground bg-background/70 px-2 py-0.5 rounded-lg">
                {notification?.ticket_number}
              </span>
            )}
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full bg-background/60 ${cfg?.color}`}>
              {notification?.stage}
            </span>
            <span className="text-[10px] text-muted-foreground ml-auto">{timeAgo(notification?.created_at)}</span>
          </div>
          {notification?.project_name && (
            <p className="text-xs font-medium text-foreground truncate mb-0.5">
              {notification?.project_name}
            </p>
          )}
          <p className="text-xs text-foreground/70 leading-relaxed line-clamp-2">
            {notification?.message}
          </p>
        </div>
      </div>

      {/* Action button */}
      <div className="px-3 pb-3">
        <button
          onClick={() => onTicketClick(notification)}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${cfg?.border} bg-background/60 text-xs font-semibold ${cfg?.color} hover:bg-background/90 transition-colors`}
        >
          <Icon name={cfg?.actionIcon} size={12} />
          {cfg?.actionLabel}
          <Icon name="ArrowRight" size={11} className="ml-auto opacity-60" />
        </button>
      </div>
    </div>
  );
};

// ─── Main Popup ───────────────────────────────────────────────────────────────

const LoginNotificationPopup = ({ notifications = [], onAction, onDismiss }) => {
  const navigate = useNavigate();

  // Group by stage for summary chips
  const stageSummary = useMemo(() => {
    const map = {};
    notifications?.forEach(n => {
      if (!map?.[n?.stage]) map[n?.stage] = [];
      map?.[n?.stage]?.push(n);
    });
    return map;
  }, [notifications]);

  const totalCount = notifications?.length;

  const handleGoToPendingTasks = () => {
    onAction?.();
    navigate('/notification-history');
  };

  const handleTicketClick = (notification) => {
    onAction?.();
    if (notification?.action_url) {
      navigate(notification?.action_url);
    } else {
      navigate('/notification-history');
    }
  };

  if (totalCount === 0) return null;

  // Show first 4 notifications as cards, rest as count
  const visibleNotifications = notifications?.slice(0, 4);
  const hiddenCount = totalCount - visibleNotifications?.length;

  return (
    // Backdrop
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-warning/15">
              <Icon name="BellRing" size={20} className="text-warning" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground leading-tight">
                Pending Tasks Require Your Action
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                You have{' '}
                <span className="font-semibold text-warning">{totalCount}</span>{' '}
                {totalCount === 1 ? 'request' : 'requests'} pending your action.
              </p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Stage Summary Chips */}
        {Object.keys(stageSummary)?.length > 0 && (
          <div className="flex flex-wrap gap-2 px-6 py-3 border-b border-border bg-muted/20">
            {Object.entries(stageSummary)?.map(([stage, items]) => {
              const cfg = STAGE_CONFIG?.[stage] || DEFAULT_STAGE;
              return (
                <span
                  key={stage}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}
                >
                  <Icon name={cfg?.icon} size={11} />
                  {stage} ({items?.length})
                </span>
              );
            })}
          </div>
        )}

        {/* Notification Cards with action buttons */}
        <div className="overflow-y-auto max-h-[380px] py-2">
          {visibleNotifications?.map((notification) => {
            const cfg = STAGE_CONFIG?.[notification?.stage] || DEFAULT_STAGE;
            return (
              <NotificationCard
                key={notification?.id}
                notification={notification}
                cfg={cfg}
                onAction={onAction}
                onTicketClick={handleTicketClick}
              />
            );
          })}
          {hiddenCount > 0 && (
            <div
              onClick={handleGoToPendingTasks}
              className="mx-4 my-2 flex items-center justify-center gap-2 p-3 rounded-xl bg-muted/40 border border-border cursor-pointer hover:bg-muted/60 transition-colors"
            >
              <Icon name="MoreHorizontal" size={14} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-medium">
                +{hiddenCount} more notification{hiddenCount !== 1 ? 's' : ''} — click to view all
              </span>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-border bg-muted/10">
          <button
            onClick={handleGoToPendingTasks}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            <Icon name="ListTodo" size={15} />
            Go to Pending Tasks
          </button>
          <button
            onClick={onDismiss}
            className="px-4 py-2.5 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            Remind Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginNotificationPopup;
